// src/pages/help/topics/chatIa.js
// Guide « Chat IA ». Contenu validé — toute modification se fait ici.
export const fr = {
  title: "Chat IA",
  tagline: "Assistant intégré : questions générales, navigation, exécution",
  readMin: 3,
  intro:
    "Le Chat IA FullMetrix est un assistant intégré à la plateforme. Il répond à certaines questions générales, aide à retrouver des pages ou fonctionnalités grâce à des raccourcis, et peut transmettre certaines demandes d'exécution vers un compte MT5 connecté. Il est encore en cours d'amélioration : ses réponses doivent être vérifiées.",
  brief: [
    "Poser des questions générales et se repérer dans la plateforme",
    "Utiliser les raccourcis pour ouvrir directement une fonctionnalité",
    "Envoyer une demande d'exécution sur un compte MT5 connecté",
  ],
  steps: [
    {
      title: "Poser la question ou utiliser un raccourci",
      text: "Les raccourcis sous la barre ouvrent directement la page concernée, sans passer par une réponse de l'IA.",
    },
    {
      title: "Vérifier l'information avant d'agir",
      text: "Pour une information précise sur une fonctionnalité, la documentation officielle du Centre d'aide fait foi.",
    },
    {
      title: "Contrôler chaque demande d'exécution",
      text: "Compte sélectionné, actif, sens, volume, Stop Loss et Take Profit doivent être relus avant validation.",
    },
  ],
  tips: [
    "Le Chat IA est un outil d'assistance : il ne remplace pas la vérification personnelle de l'utilisateur.",
    "Ses réponses peuvent être incomplètes ou incorrectes — la documentation de chaque fonctionnalité reste la référence.",
    "En cas de doute, le support WhatsApp donne la réponse la plus rapide.",
  ],
  faq: [
    {
      q: "Qu'est-ce que le Chat IA FullMetrix ?",
      a:
        "Le Chat IA FullMetrix est un assistant intégré à la plateforme.\n\n" +
        "Il permet principalement de :\n" +
        "• répondre à certaines questions générales\n" +
        "• aider l'utilisateur à retrouver certaines pages ou fonctionnalités grâce à des raccourcis\n" +
        "• transmettre certaines demandes d'exécution vers un compte MT5 connecté",
    },
    {
      q: "Le Chat IA peut-il prendre des positions ?",
      a:
        "Oui.\n\n" +
        "Lorsque l'Agent IA MT5 est correctement installé et connecté, l'utilisateur peut envoyer une demande depuis le Chat IA pour ouvrir une position sur son compte de trading.\n\n" +
        "L'utilisateur doit toujours vérifier attentivement :\n" +
        "• le compte sélectionné\n" +
        "• l'actif\n" +
        "• le sens de la position\n" +
        "• le volume\n" +
        "• le Stop Loss\n" +
        "• le Take Profit",
    },
    {
      q: "Le Chat IA peut-il répondre à toutes les questions sur FullMetrix ?",
      a:
        "Non.\n\n" +
        "Le Chat IA peut ne pas maîtriser certaines informations précises concernant FullMetrix ou ses différentes fonctionnalités.\n\n" +
        "Ses réponses peuvent être :\n" +
        "• incomplètes\n" +
        "• imprécises\n" +
        "• mal interprétées\n" +
        "• incorrectes\n\n" +
        "Il est donc recommandé de consulter directement la documentation officielle de chaque fonctionnalité pour obtenir des informations plus fiables.",
    },
    {
      q: "Les réponses du Chat IA sont-elles toujours fiables ?",
      a:
        "Non.\n\n" +
        "Le Chat IA est encore en cours d'amélioration.\n\n" +
        "L'utilisateur doit toujours vérifier les informations importantes avant de :\n" +
        "• prendre une décision\n" +
        "• modifier un paramètre\n" +
        "• exécuter une position\n" +
        "• appliquer une recommandation\n\n" +
        "Le Chat IA reste un outil d'assistance. Il ne remplace pas la vérification personnelle de l'utilisateur.",
    },
    {
      q: "Le Chat IA est-il encore en développement ?",
      a:
        "Oui.\n\n" +
        "De nouvelles fonctionnalités et améliorations pourront être ajoutées dans les prochaines mises à jour.\n\n" +
        "Pour le moment, son utilisation reste principalement limitée à :\n" +
        "• certaines questions générales\n" +
        "• l'aide à la navigation\n" +
        "• les raccourcis vers certaines pages\n" +
        "• certaines demandes d'exécution sur un compte MT5 connecté",
    },
    {
      q: "Que faire en cas de doute ou de problème ?",
      a:
        "Pour obtenir une information précise sur FullMetrix, il est recommandé de :\n" +
        "• consulter la documentation officielle de la fonctionnalité concernée\n" +
        "• vérifier les informations affichées sur la plateforme\n" +
        "• contacter le support par WhatsApp pour obtenir une réponse plus rapide",
    },
  ],
  related: ["agent-ia-mt5", "execution", "journal-auto"],
};

export const en = {
  title: "AI Chat",
  tagline: "Built-in assistant: general questions, navigation, execution",
  readMin: 3,
  intro:
    "The FullMetrix AI Chat is an assistant built into the platform. It answers some general questions, helps you find pages or features through shortcuts, and can forward some execution requests to a connected MT5 account. It is still being improved: its answers must be verified.",
  brief: [
    "Ask general questions and find your way around the platform",
    "Use the shortcuts to open a feature directly",
    "Send an execution request to a connected MT5 account",
  ],
  steps: [
    {
      title: "Ask the question or use a shortcut",
      text: "The shortcuts under the bar open the matching page directly, without going through an AI answer.",
    },
    {
      title: "Verify the information before acting",
      text: "For precise information about a feature, the official Help center documentation is the reference.",
    },
    {
      title: "Check every execution request",
      text: "Selected account, asset, direction, volume, Stop Loss and Take Profit must be reviewed before confirming.",
    },
  ],
  tips: [
    "The AI Chat is a support tool: it does not replace your own verification.",
    "Its answers can be incomplete or incorrect — each feature's documentation remains the reference.",
    "When in doubt, WhatsApp support gives the fastest answer.",
  ],
  faq: [
    {
      q: "What is the FullMetrix AI Chat?",
      a:
        "The FullMetrix AI Chat is an assistant built into the platform.\n\n" +
        "It mainly lets you:\n" +
        "• get answers to some general questions\n" +
        "• find pages or features through shortcuts\n" +
        "• forward some execution requests to a connected MT5 account",
    },
    {
      q: "Can the AI Chat open positions?",
      a:
        "Yes.\n\n" +
        "When the MT5 AI Agent is properly installed and connected, you can send a request from the AI Chat to open a position on your trading account.\n\n" +
        "You must always carefully check:\n" +
        "• the selected account\n" +
        "• the asset\n" +
        "• the direction of the position\n" +
        "• the volume\n" +
        "• the Stop Loss\n" +
        "• the Take Profit",
    },
    {
      q: "Can the AI Chat answer every question about FullMetrix?",
      a:
        "No.\n\n" +
        "The AI Chat may not master some precise information about FullMetrix or its features.\n\n" +
        "Its answers can be:\n" +
        "• incomplete\n" +
        "• imprecise\n" +
        "• misinterpreted\n" +
        "• incorrect\n\n" +
        "We therefore recommend reading the official documentation of each feature for more reliable information.",
    },
    {
      q: "Are the AI Chat answers always reliable?",
      a:
        "No.\n\n" +
        "The AI Chat is still being improved.\n\n" +
        "You must always verify important information before:\n" +
        "• making a decision\n" +
        "• changing a setting\n" +
        "• executing a position\n" +
        "• applying a recommendation\n\n" +
        "The AI Chat remains a support tool. It does not replace your own verification.",
    },
    {
      q: "Is the AI Chat still under development?",
      a:
        "Yes.\n\n" +
        "New features and improvements may be added in upcoming updates.\n\n" +
        "For now, its use is mainly limited to:\n" +
        "• some general questions\n" +
        "• navigation help\n" +
        "• shortcuts to some pages\n" +
        "• some execution requests on a connected MT5 account",
    },
    {
      q: "What should I do in case of doubt or a problem?",
      a:
        "For precise information about FullMetrix, we recommend that you:\n" +
        "• read the official documentation of the feature involved\n" +
        "• check the information displayed on the platform\n" +
        "• contact support on WhatsApp for a faster answer",
    },
  ],
  related: ["agent-ia-mt5", "execution", "journal-auto"],
};
